/**
 * Network Speed Detection Utility
 *
 * Determines if the user's network is fast enough to load video content.
 * Uses a hybrid approach combining:
 * 1. Network Information API (navigator.connection)
 * 2. Preflight speed test
 * 3. Conservative defaults
 */

// Extend Navigator interface for TypeScript
interface NetworkInformation {
  effectiveType?: '4g' | '3g' | '2g' | 'slow-2g';
  downlink?: number; // Mbps
  saveData?: boolean;
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation;
  mozConnection?: NetworkInformation;
  webkitConnection?: NetworkInformation;
}

export type NetworkSpeed = 'fast' | 'slow';

// Session storage key for caching
const CACHE_KEY = 'alright_network_speed';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedResult {
  speed: NetworkSpeed;
  timestamp: number;
}

/**
 * Get cached network speed if available and not expired
 */
function getCachedSpeed(): NetworkSpeed | null {
  if (typeof window === 'undefined') return null;

  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const result: CachedResult = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is still valid
    if (now - result.timestamp < CACHE_DURATION) {
      return result.speed;
    }

    // Cache expired
    sessionStorage.removeItem(CACHE_KEY);
    return null;
  } catch {
    return null;
  }
}

/**
 * Cache the network speed result
 */
function cacheSpeed(speed: NetworkSpeed): void {
  if (typeof window === 'undefined') return;

  try {
    const result: CachedResult = {
      speed,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(result));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Strategy 1: Use Network Information API
 * Returns 'fast' | 'slow' | null (if unsupported)
 */
function detectViaNetworkInfoAPI(): NetworkSpeed | null {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return null;
  }

  const nav = navigator as NavigatorWithConnection;
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection;

  if (!connection) {
    return null;
  }

  // If user has enabled data saver mode, always use slow network
  if (connection.saveData) {
    return 'slow';
  }

  // Check effective connection type
  if (connection.effectiveType) {
    const effectiveType = connection.effectiveType;

    if (effectiveType === '4g') {
      return 'fast';
    }

    if (effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g') {
      return 'slow';
    }
  }

  // Check downlink speed (in Mbps)
  if (typeof connection.downlink === 'number') {
    // Thresholds:
    // - >= 1.5 Mbps → fast (video streaming)
    // - < 1.5 Mbps → slow (image only)
    return connection.downlink >= 1.5 ? 'fast' : 'slow';
  }

  return null;
}

/**
 * Strategy 2: Preflight speed test
 * Makes a HEAD request to measure response time
 */
async function detectViaPreflightTest(videoUrl: string): Promise<NetworkSpeed> {
  const TIMEOUT_MS = 3000; // 3 seconds
  const FAST_THRESHOLD_MS = 2000; // 2 seconds

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    const startTime = performance.now();

    await fetch(videoUrl, {
      method: 'HEAD',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    const duration = performance.now() - startTime;

    // If response came back quickly, network is fast
    return duration < FAST_THRESHOLD_MS ? 'fast' : 'slow';
  } catch (error) {
    // On timeout or error, assume slow network
    return 'slow';
  }
}

/**
 * Main function: Detect network speed using hybrid approach
 *
 * @param videoUrl - Optional URL to test (for preflight strategy)
 * @returns Promise<NetworkSpeed> - 'fast' or 'slow'
 */
export async function detectNetworkSpeed(videoUrl?: string): Promise<NetworkSpeed> {
  // Check cache first
  const cached = getCachedSpeed();
  if (cached) {
    return cached;
  }

  // Strategy 1: Try Network Information API first (instant)
  const apiResult = detectViaNetworkInfoAPI();
  if (apiResult) {
    cacheSpeed(apiResult);
    return apiResult;
  }

  // Strategy 2: Fallback to preflight test (if video URL provided)
  if (videoUrl) {
    const testResult = await detectViaPreflightTest(videoUrl);
    cacheSpeed(testResult);
    return testResult;
  }

  // Strategy 3: Conservative default - assume slow network
  const defaultSpeed: NetworkSpeed = 'slow';
  cacheSpeed(defaultSpeed);
  return defaultSpeed;
}

/**
 * Utility: Check if Network Information API is supported
 */
export function isNetworkInfoAPISupported(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const nav = navigator as NavigatorWithConnection;
  return !!(nav.connection || nav.mozConnection || nav.webkitConnection);
}

/**
 * Utility: Get current network information (for debugging)
 */
export function getNetworkInfo(): NetworkInformation | null {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return null;
  }

  const nav = navigator as NavigatorWithConnection;
  return nav.connection || nav.mozConnection || nav.webkitConnection || null;
}

/**
 * Preload media (image or video) into browser cache
 * This ensures faster display when the media is actually needed
 *
 * @param url - URL of the media to preload
 * @param type - Type of media ('image' or 'video')
 */
export async function preloadMedia(url: string, type: 'image' | 'video'): Promise<void> {
  if (typeof window === 'undefined' || !url) return;

  try {
    if (type === 'image') {
      // Preload image using Image constructor
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to preload image'));
        img.src = url;
      });
    } else if (type === 'video') {
      // Preload video by fetching and caching response
      const response = await fetch(url, {
        method: 'GET',
        cache: 'force-cache',
      });

      if (!response.ok) {
        throw new Error('Failed to preload video');
      }

      // Read the response to ensure it's cached
      await response.blob();
    }
  } catch (error) {
    // Silently fail - preload is an optimization, not critical
    console.warn(`Failed to preload ${type}:`, error);
  }
}
